import * as React from "react";
import Button from "../../components/Button/Button";
import { SGBSColor, SGBSSize } from "../../common/common.typings";

export interface InputSelectProps {
  inError?: boolean;
  iconName?: string | null;
  placeholder?: string;
  showToggleButton?: boolean;
  isOutline?: boolean;
  color?: SGBSColor;
  size?: SGBSSize;
  disabled?: boolean;
  showClearButton?: boolean;
  noSearch?: boolean;
  inputAppendComponent?: React.ReactElement;
  inputSearchMode?: boolean;
  autofocus?: boolean;
}

type Props = InputSelectProps & {
  hasFocus: boolean;
  isDropdownOpen: boolean;
  readonly?: boolean;
  displayedText?: string;
  searchTerm?: string;
  selectedItemsCount?: number;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  onToggleDropdown: (isDropdownOpen: boolean) => void;
  onToggleFocus: (hasFocus: boolean) => void;
  onSearchTermChange: (searchTerm: string) => void;
  onClearSelection: () => void;
};
function SelectInput({
  size = "md",
  iconName,
  placeholder = "",
  inError = false,
  isOutline = false,
  readonly = false,
  showToggleButton = true,
  hasFocus = false,
  displayedText = "",
  searchTerm = "",
  selectedItemsCount = 0,
  isDropdownOpen = false,
  disabled = false,
  inputRef,
  showClearButton,
  inputAppendComponent,
  onClearSelection,
  onSearchTermChange,
  onToggleDropdown,
  onToggleFocus,
  color,
  noSearch,
  inputSearchMode = false,
  autofocus = false
}: Props): React.ReactElement {
  const [hover, setHover] = React.useState<boolean>(false);
  const inputGroupStyle =
    disabled && inError
      ? { boxShadow: `inset 0 -0.0625rem 0 0 #dd3724` }
      : hasFocus
        ? { boxShadow: `inset 0 0 0 2px ${inError ? "#dd3724" : ""}` }
        : {};
  const hasIcon = iconName !== null && iconName !== undefined && iconName.trim().length > 1;
  const inputStyle = noSearch ? { flex: "1 1 0px", cursor: "pointer" } : { flex: "1 1 120px" };

  const handleOnToggle = (): void => {
    onToggleDropdown(!isDropdownOpen);
  };

  const handleFocus = (isHasFocus: boolean): void => {
    if (!disabled) {
      onToggleFocus(isHasFocus);
    }
  };

  const handleOpenDropdown = (): void => {
    if (!disabled) {
      onToggleDropdown(true);
    }
  };

  const handleOnClear = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    onClearSelection();
  };

  if (readonly) {
    return (
      <div className={`input-group input-group-${size}`}>
        <input type="text" className="form-control" value={displayedText} readOnly />
      </div>
    );
  }

  const toggleHover = (fromHover: boolean): void => {
    setHover(fromHover);
  };

  return (
    <div
      className={`input-group input-group-${size} ${disabled ? "disabled" : ""}`}
      onMouseOver={(): void => toggleHover(true)}
      onMouseLeave={(): void => toggleHover(false)}
    >
      {hasIcon && (
        <div className="input-group-prepend input-group-merged" style={disabled ? { opacity: 0.4 } : {}}>
          <i className={`icon text-secondary ${size === "sm" ? "" : size === "md" ? " icon-sm" : "icon-md"}`}>
            {iconName}
          </i>
        </div>
      )}
      {inputSearchMode ? (
        <input
          onFocus={(): void => handleFocus(true)}
          onBlur={(): void => handleFocus(false)}
          ref={inputRef}
          type="search"
          style={inputStyle}
          className="form-control"
          disabled={disabled}
          autoFocus={autofocus}
          placeholder={noSearch && !!displayedText ? "" : placeholder}
          value={searchTerm}
          readOnly={noSearch}
          onChange={(e): void => onSearchTermChange(e.target.value)}
        />
      ) : (
        <div
          style={{ ...inputGroupStyle, borderRadius: 0 }}
          className={`d-flex form-control  align-items-center pr-0 ${inError ? "is-invalid" : ""} ${isOutline ? "form-control-alt" : ""
            }`}
        >
          {displayedText && (
            <div
              onClick={handleOpenDropdown}
              style={{ cursor: disabled ? "" : "pointer" }}
              className={`text-${color} text-truncate ${hasIcon ? "mx-2" : "mr-2"}`}
              title={displayedText}
            >
              {displayedText}
            </div>
          )}
          <input
            onFocus={(): void => handleFocus(true)}
            onBlur={(): void => handleFocus(false)}
            ref={inputRef}
            type="text"
            style={inputStyle}
            className="form-control-plaintext pl-2"
            disabled={disabled}
            placeholder={noSearch && !!displayedText ? "" : placeholder}
            value={searchTerm}
            readOnly={noSearch}
            autoFocus={autofocus}
            onChange={(e): void => onSearchTermChange(e.target.value)}
          />
          <div className="d-flex flex-nowrap align-items-center">
            {!inputSearchMode && showClearButton && (hover || hasFocus) && selectedItemsCount > 0 && !disabled && (
              <Button
                btnType="link"
                size={size}
                icon="cancel"
                onClick={handleOnClear}
                disabled={disabled}
                style={{ zIndex: 10 }}
              />
            )}
            {showToggleButton && (
              <Button
                btnType="flat"
                size={size}
                className="d-flex flex-center m-0"
                icon={isDropdownOpen ? "arrow_drop_up" : "arrow_drop_down"}
                onClick={handleOnToggle}
                disabled={disabled}
              />
            )}
            {inputAppendComponent}
          </div>
        </div>
      )}
    </div>
  );
}

export default SelectInput;
